import {
  Box,
  Button,
  Card,
  Checkbox,
  Separator,
  Text,
  VStack,
} from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import { useEffect, useState, useMemo } from "react";
import { t } from "i18next";
import { OsaWaySnapshot } from "../../../../../coding/models/OsaWaySnapshot";
import { OsaWayApi } from "../../../../../osaway/services/OsaWayApi";
import { OsaWaySnapshotApi } from "../../../../../coding/services/OsaWaySnapshotApi";
import { LoadingScreen } from "../../../../../shared/views/LoadingScreen/LoadingScreen";
import { useStores } from "../../../../../shared/stores/RootStore";
import { toast } from "../../../../../shared/views/CustomToaster";
import { CustomDialog } from "../../../../../shared/views/CustomDialog";
import { OsaWaySnapshotPreview } from "./OsaWaySnapshotPreview";

interface Props {
  onClose: () => any;
}
const CodingHistoryModalBase: React.FC<Props> = ({ onClose }) => {
  const { osaWayStore, codingStore } = useStores();
  const [snapshots, setSnapshots] = useState<OsaWaySnapshot[]>();
  const [selectedId, setSelectedId] = useState<string>();
  const [loading, setLoading] = useState(true);

  const selectedOsaWay = osaWayStore.getSelected();

  useEffect(() => {
    OsaWayApi.listOsaWaySnapshots(selectedOsaWay!.id!).then(({ data }) => {
      setSnapshots(data);
      setLoading(false);
    });
    // eslint-disable-next-line
  }, [selectedOsaWay]);

  const selectedSnapshot = useMemo(
    () => snapshots?.find((snap) => snap.id === selectedId),
    [snapshots, selectedId]
  );

  const submit = async () => {
    setLoading(true);
    await OsaWaySnapshotApi.apply(selectedId!)
      .then(() => {
        toast("success", t("coding:history.submitSuccess"));
        codingStore.fetchAttributesByOsaWayId(selectedOsaWay!.id);
        onClose();
      })
      .catch(() => {
        toast("error", t("coding:history.submitError"));
        setLoading(false);
      });
  };

  return (
    <CustomDialog
      size="xl"
      header={t("coding:history.title")}
      footer={
        <Button
          colorPalette="primary"
          disabled={!selectedId}
          loading={loading}
          onClick={submit}
        >
          Apply selected version
        </Button>
      }
      onClose={onClose}
    >
      <Box overflow="hidden" display="flex">
        <Card.Root
          variant="outline"
          display="flex"
          flexDirection="row"
          flexGrow={1}
        >
          {snapshots ? (
            <>
              <VStack
                flexGrow={1}
                flexShrink={0}
                overflow="auto"
                gap={0}
                separator={<Separator />}
                flexDirection="column-reverse"
              >
                {snapshots.map((snapshot, i) => {
                  const createdAt = new Date(snapshot.createdAt);
                  const isLast = i === snapshots.length - 1;
                  return (
                    <Box
                      key={i}
                      px={4}
                      py={2}
                      width="100%"
                      bg={
                        snapshot.id === selectedId
                          ? "primary.50"
                          : "transparent"
                      }
                    >
                      <Checkbox.Root
                        disabled={isLast}
                        name={snapshot.id}
                        checked={snapshot.id === selectedId}
                        onCheckedChange={() => {
                          setSelectedId((prev) =>
                            prev === snapshot.id ? undefined : snapshot.id
                          );
                        }}
                        colorPalette="primary"
                        width="100%"
                      >
                        <Checkbox.HiddenInput />
                        <Checkbox.Control />
                        <Checkbox.Label>
                          <Box pl={2} fontSize="sm">
                            <Text>
                              {`${createdAt.toDateString()}, ${createdAt.toLocaleTimeString()}`}
                              {isLast &&
                                ` (${t("coding:history.currentVersion")})`}
                            </Text>
                          </Box>
                        </Checkbox.Label>
                      </Checkbox.Root>
                    </Box>
                  );
                })}
              </VStack>
              <Box>
                <Separator orientation="vertical" />
              </Box>
              <OsaWaySnapshotPreview snapshot={selectedSnapshot} />
            </>
          ) : (
            <Box flexGrow={1} position="relative" height={32}>
              <LoadingScreen />
            </Box>
          )}
        </Card.Root>
      </Box>
    </CustomDialog>
  );
};

export const CodingHistoryModal = observer(CodingHistoryModalBase);
